import { CommerceService } from "@/services/commerce"

import { getProductSelections } from "../../cart/utils/getItemsWithSelections"
import { getStoresByMultipleIdsString } from "./getStores"

const commerceApi = CommerceService.api()

/**
 * Method for forming the query object for price retrieval
 *
 * @param {string} sku
 * @param {string[]|false} channels
 * @param {string|false} group
 * @param {string|false} country
 *
 * @returns {object}
 */
const formQuery = (sku, channels, group, country) => {
  let channelString = "channel is not defined"

  if (channels && channels.length > 0) {
    let channelIds = channels.map((channel) => `"${channel.distributionChannelId}"`)
    channelIds = channelIds.join(",")

    channelString = `channel(id in (${channelIds}))`
  }

  const skuString = `sku="${sku}"`
  const groupString = group ? `customerGroup(id="${group}")` : "customerGroup is not defined"
  const countryString = country ? `country="${country}"` : "country is not defined"

  return {
    queryArgs: {
      where: `${skuString} and ${channelString} and ${countryString} and ${groupString}`,
    },
  }
}

/**
 * Method to retrieve prices, mainly used for cart and price listing for products.
 *
 * @param {string} sku
 * @param {array|null} channels
 * @param {object} group
 * @param {string} country
 *
 * @return {Promise<price>|null}
 */
export const getSkuPrice = async ({ sku, channels, group, country }) => {
  if (typeof sku !== "string" && sku === "") return null

  let price = null
  let customerPrice = null

  // first case, price matches everything (SOKO condition)
  price = (
    await commerceApi
      .standalonePrices()
      .get(formQuery(sku, channels, group?.id, country))
      .execute()
  )?.body?.results

  customerPrice = await findRightPrice(price, sku, channels)

  if (customerPrice) return customerPrice

  // second case, price matches one of the channels and group
  price = (
    await commerceApi
      .standalonePrices()
      .get(formQuery(sku, channels, group?.id, false))
      .execute()
  )?.body?.results

  customerPrice = await findRightPrice(price, sku, channels)

  if (customerPrice) return customerPrice

  // if none of the cases were successful, retrieve the standard price
  price = (
    await commerceApi
      .standalonePrices()
      .get(formQuery(sku, false, false, false))
      .execute()
  )?.body?.results

  // no price was found for this listing, return null
  if (price.length < 1) return null

  // default price
  return price[0]
}

/**
 * Takes either a custom price or finds sku price based on the sku channel group and country
 * Applies discount percentage to the price if it is available
 * Forms money object
 *
 * @param {string} sku
 * @param {array|null} channels
 * @param {object} group
 * @param {string} country
 * @param {number} discountPercent
 * @param {number} customPrice
 * @param {string} currencyCode
 * @param {number} quantity
 *
 * @returns {object|null}
 */
export const getSkuCalculatedPrice = async ({
  sku,
  channels,
  group,
  country,
  discountPercent,
  customPrice,
  currencyCode,
  articleConfig,
  quantity = 1,
}) => {
  let centAmount = 0
  let price = null
  let discountedPrice = 0
  let fractionDigits = 2
  let factor = Number(articleConfig?.amountBilling) || 1
  let priceValue = null
  const sellableInDecimals = articleConfig?.sellableInDecimals

  // dont forget to cover case when price is qm

  // there could be either custom price (set by adm manually in the application)
  // or sku price taken from the commercetools
  if (typeof customPrice === "number") {
    // Prevent javascript from doing javascript things...
    // Like converting 1.15 to 1.149999999
    // if item is sellable in decimals, fe passes value for 100 items devided by 100
    // this lead to 6.5437 instead of rounding this value, multiply it by 100 and add 2 to fraction digits

    // Multiply by 1000, round, then divide by 10 to avoid floating point issues
    centAmount = sellableInDecimals ? Math.round(customPrice * 1000) / 10 : Math.round(customPrice)
    // centAmount = sellableInDecimals ? Math.round(customPrice * 100) : Math.round(customPrice)
    fractionDigits = sellableInDecimals ? fractionDigits + 2 : fractionDigits
  } else {
    // covers case when no custom price is present (most usual case)
    price = await getSkuPrice({ sku, channels, group, country })

    if (!price) {
      return null
    }

    centAmount = Number(price?.value?.preciseAmount) || Number(price?.value?.centAmount)
    fractionDigits = Number(price?.value?.fractionDigits) || fractionDigits
    priceValue = price

    // yes, the price even has tiers based on the quantity...
    if (price?.tiers && price?.tiers?.length >= 1) {
      priceValue = getPriceValueFromTiers({ quantity, price })

      centAmount = Number(priceValue?.value?.centAmount)
      fractionDigits = Number(priceValue?.value?.fractionDigits) || fractionDigits
    }
  }

  const skuPrice = centAmount / Math.pow(10, fractionDigits)

  // apply the discount to the price if percent is available
  if (discountPercent) {
    discountedPrice = centAmount - centAmount * (Number(discountPercent) / 100)
    // round it to 2 digits after comma and multiply from 100 because commercetools only accept int values
    centAmount = +(Math.round(discountedPrice + "e+2") + "e-2") * 100
    // since fraction digits were already 2 (default value), add 2 more after rounding the price
    fractionDigits = Number(fractionDigits) + 2
  }

  const factorApplied = factor !== 1 || sellableInDecimals

  if (factorApplied) {
    const factoredPrice = centAmount * factor
    centAmount = +(Math.round(factoredPrice + "e+2") + "e-2") * 100
    fractionDigits = Number(fractionDigits) + 2
  }

  const isHighPrecision = factorApplied || discountPercent

  const priceKeyAmountPrefix = sellableInDecimals
    ? priceValue?.value?.preciseAmount || priceValue?.value?.centAmount
    : priceValue?.value?.centAmount

  const priceKey = priceValue?.tier
    ? `tier-${priceKeyAmountPrefix}-${price?.key}`
    : `${priceKeyAmountPrefix}-${price?.key}`

  if (isHighPrecision) {
    const recalculatedPrice = recalculateRoundedPrice(centAmount, fractionDigits)

    centAmount = sellableInDecimals ? recalculatedPrice.centAmount + 1 : recalculatedPrice.centAmount
    fractionDigits = recalculatedPrice.fractionDigits
  }

  return {
    key: typeof customPrice === "number" ? "custom" : priceKey,
    skuPrice,
    value: {
      ...(isHighPrecision && { preciseAmount: Math.round(centAmount) }),
      ...(!isHighPrecision && { centAmount }),
      type: isHighPrecision ? "highPrecision" : "centPrecision",
      currencyCode: currencyCode || price.currencyCode,
      fractionDigits,
    },
  }
}

/**
 * Finds the top tier based on quantity and returns value
 *
 * @param {{quantity: number, price: object}}
 *
 * @returns {{tier: boolean, value: object}}
 */
const getPriceValueFromTiers = ({ quantity, price }) => {
  // nothing to do
  if (!price) return null
  // nothing to compare
  if (!quantity || price?.tiers?.length <= 0) return { tier: false, value: price.value }

  let tiers = price.tiers
  // filter all the tiers that are less than current quantity
  tiers = tiers.filter((tier) => tier?.minimumQuantity <= quantity)

  // no item was found in array, return value
  if (tiers.length <= 0) return { tier: false, value: price.value }

  // find the top tier based on all the tiers that are left after filtering
  const topTier = tiers.reduce((prev, current) =>
    prev && prev.minimumQuantity > current.minimumQuantity ? prev : current,
  )

  // no top tier, return original price
  if (!topTier) return { tier: false, value: price.value }

  return { tier: true, value: topTier.value }
}

/**
 * Finds the most specific price from an array of prices
 * A price is considered more specific if it has:
 * - validFrom and validUntil (time validity)
 * - tiers (quantity-based pricing)
 *
 * @param {array} prices - Array of price objects
 * @returns {object}
 */
const getMostSpecificPrice = (prices) => {
  if (!prices || prices.length === 0) return null
  if (prices.length === 1) return prices[0]

  return prices.reduce((mostSpecific, current) => {
    let currentScore = 0
    let mostSpecificScore = 0

    // Score based on time validity (validFrom and validUntil)
    if (current?.validFrom && current?.validUntil) {
      currentScore += 3
    }
    if (mostSpecific?.validFrom && mostSpecific?.validUntil) {
      mostSpecificScore += 3
    }

    // Score based on tiers (quantity-based pricing)
    if (current?.tiers && current.tiers.length > 0) {
      currentScore += 2
    }
    if (mostSpecific?.tiers && mostSpecific.tiers.length > 0) {
      mostSpecificScore += 2
    }

    // Additional score for having a value
    if (current?.value) {
      currentScore += 1
    }
    if (mostSpecific?.value) {
      mostSpecificScore += 1
    }

    return currentScore > mostSpecificScore ? current : mostSpecific
  })
}

/**
 * Method to find the right price based on the product selection if multiple prices were found
 *
 * @param {array} prices
 * @param {string} sku
 * @param {array} channels
 *
 * @returns {Promise<object>}
 */
const findRightPrice = async (prices, sku, channels) => {
  if (prices.length === 0) return null

  const dateValidPrices = isPriceExpiredOrNotValid(prices)

  if (dateValidPrices.length === 0) return null

  // top priority prices (VERBAND)
  const specialPrice = dateValidPrices.filter((priceList) => priceList?.key?.includes("VERBAND"))

  if (specialPrice.length > 0) return getMostSpecificPrice(specialPrice)

  const storeKeys = channels?.map((channel) => `"${channel.key}"`).join(", ")

  const storeDataByKeys = await getStoresByMultipleIdsString(storeKeys)

  if (!storeDataByKeys) return dateValidPrices[0]

  const selectionIds = await getProductSelections(sku)

  // wrong configuration, but return first item
  if (!selectionIds) return dateValidPrices[0]

  const productSkuIncluded = selectionIds?.map((productSelectionId) => `"${productSelectionId}"`).join(",")

  const selection = await getSelection(productSkuIncluded)

  return findPriceByProductSelectionAndChannel({
    storeDataByKeys,
    selection,
    prices: dateValidPrices,
  })
}

/**
 * Retrieves product selection by id or ids
 *
 * @param {string} productSkuIncluded
 *
 * @returns {Promise<ProductSelection[]>}
 */
const getSelection = async (productSkuIncluded) => {
  return (
    await commerceApi
      .productSelections()
      .get({ queryArgs: { where: `id in (${productSkuIncluded})` } })
      .execute()
  )?.body?.results
}

const findPriceByProductSelectionAndChannel = ({ storeDataByKeys, selection, prices }) => {
  // returns an array of objects containing the store key and its product selection ID (AHG)
  const productSelectionIdByStore = storeDataByKeys.flatMap(({ key, productSelections }) =>
    productSelections?.map(({ productSelection }) => ({ key, id: productSelection?.id })),
  )

  let selectedPrice = selection.map(({ id }) => {
    const store = productSelectionIdByStore?.find((item) => item?.id === id)
    // extracts the price list key from the given store key
    // e.g. "VERBAND-26640-priceList3" => "priceList3"

    if (store?.key?.includes("purchasePrice")) {
      return prices.find((priceList) => priceList?.key?.includes("purchasePrice"))
    }

    if (store?.key?.includes("minimumSellingPrice")) {
      return prices.find((priceList) => priceList?.key?.includes("minimumSellingPrice"))
    }

    if (store?.key?.includes("suggestedSellingPriceNet")) {
      return prices.find((priceList) => priceList?.key?.includes("suggestedSellingPriceNet"))
    }

    const priceListName = store?.key?.match(/priceList(\d+)/)

    if (!priceListName) return null

    const price = prices.find((priceList) => priceList?.key?.includes(priceListName[0]))

    if (!price) return null

    return price
  })

  selectedPrice = selectedPrice.filter((n) => n)

  // keep original logic of returning first item from the array
  if (selectedPrice.length === 0) return null

  return selectedPrice[0]
}

/**
 * Checks if commercetools is going to have problems while rounding the price and if so adds +1 to the centAmount
 *
 * @param {number} centAmount
 * @param {number} fractionDigits
 *
 * @returns {object: {centAmount: number, fractionDigits: number}}
 */
const recalculateRoundedPrice = (centAmount, fractionDigits) => {
  const fractionNumber = Math.pow(10, fractionDigits)
  const splitByDecimal = String(centAmount / fractionNumber).split(".")
  const valueAfterDecimal = splitByDecimal[1]

  if (splitByDecimal.length > 1 && valueAfterDecimal.length > 2 && valueAfterDecimal[2] === "5") {
    return getUpdatedCentAmount(centAmount, fractionDigits, splitByDecimal)
  }

  return { centAmount, fractionDigits }
}

/**
 * Checks if logic should be applied and returns the updated centAmount with fractionDigits value
 *
 * @param {number} centAmount
 * @param {number} fractionDigits
 * @param {[string]} splitByDecimal
 *
 * @returns {object: {centAmount: number, fractionDigits: number}}
 */
const getUpdatedCentAmount = (centAmount, fractionDigits, splitByDecimal) => {
  const stringedCents = String(centAmount)
  const targetDecimal = splitByDecimal[1][3]
  const targetIsZeroOrDoesNotExist = !targetDecimal || targetDecimal === "0"

  if (targetIsZeroOrDoesNotExist && stringedCents[fractionDigits + 1] === "0") {
    return { centAmount: centAmount + 1, fractionDigits }
  }

  if (targetIsZeroOrDoesNotExist && !stringedCents[fractionDigits + 1]) {
    return { centAmount: Number(stringedCents + "1"), fractionDigits: fractionDigits + 1 }
  }

  return { centAmount, fractionDigits }
}

/**
 * Returns prices that are not expired
 *
 * @param {array} prices
 *
 * @returns {array}
 */
const isPriceExpiredOrNotValid = (prices) => {
  if (!prices || prices.length === 0) return []

  const currentDate = new Date()

  return prices.filter((price) => {
    if (!price?.validFrom || !price?.validUntil) {
      return price
    }

    const dateValidFrom = new Date(price.validFrom)
    const dateValidTo = new Date(price.validUntil)

    if (dateValidFrom < currentDate && dateValidTo > currentDate) {
      return price
    }

    return null
  })
}

/**
 * Gets prices from the sku array
 *
 * @param {{skus: string}}
 *
 * @returns {Promise<array|null>}
 */
export const getSkuPriceList = async ({ sku }) => {
  const listPriceChannels = await getPricesChannels()

  if (listPriceChannels.length === 0) {
    return null
  }

  try {
    const channelIds = listPriceChannels.map((channel) => `"${channel.id}"`).join(",")

    const prices = (
      await commerceApi
        .standalonePrices()
        .get({
          queryArgs: {
            where: `sku="${sku}" and channel(id in (${channelIds})) or sku="${sku}" and channel is not defined`,
          },
        })
        .execute()
    )?.body?.results

    if (prices.length < 1) return null

    return prices
  } catch (err) {
    console.warn(err)
    return null
  }
}

/**
 * Gets prices from the sku array
 *
 * @param {{skus: array}}
 *
 * @returns {Promise<array|null>}
 */
export const getSkuPricesFromSkuArray = async ({ skus }) => {
  if (!skus || skus.length === 0) return null

  const listPriceChannels = await getPricesChannels()

  if (listPriceChannels.length === 0) {
    return null
  }

  try {
    const channelIds = listPriceChannels.map((channel) => `"${channel.id}"`).join(",")
    const uniqueSkus = skus.filter((value, index, array) => array.indexOf(value) === index)

    const skusListPrices = await getAllPricesFromSkuArray(uniqueSkus, channelIds, listPriceChannels.length)

    return skusListPrices
  } catch (err) {
    console.warn(err)
    return null
  }
}

/**
 * Helper for sku prices retrieval. Splits into multiple requests and retrieves all the prices
 *
 * @param {{skuList: array, channelIds: array, totalChannels: number}}
 *
 * @returns {Promise<array>}
 */
const getAllPricesFromSkuArray = async (skuList, channelIds, totalChannels) => {
  if (!skuList || skuList.length === 0) throw new Error("No sku list provided")
  if (!totalChannels) throw new Error("No channels provided")

  let results = []
  let offset = 0
  let limit = 500
  let total = totalChannels * skuList.length
  let currentLastIndex = limit

  const commerceApi = CommerceService.api()

  do {
    const skus = []

    for (let index = offset; index < currentLastIndex; index++) {
      if (skuList[index]) {
        skus.push(`"${skuList[index]}"`)
      }
    }
    if (skus.length === 0) return results

    const response = (
      await commerceApi
        .standalonePrices()
        .get({
          queryArgs: {
            where: `(sku in (${skus}) and channel(id in (${channelIds}))) or (sku in (${skus}) and channel is not defined)`,
            limit: 500,
          },
        })
        .execute()
    )?.body?.results

    if (!response || response?.length === 0) return results

    if (response?.length >= 500) {
      let secondOffset = 500
      let justDoIt = true
      while (justDoIt) {
        const secondResponse = (
          await commerceApi
            .standalonePrices()
            .get({
              queryArgs: {
                where: `(sku in (${skus}) and channel(id in (${channelIds}))) or (sku in (${skus}) and channel is not defined)`,
                limit: 500,
                offset: secondOffset,
              },
            })
            .execute()
        )?.body?.results
        if (!secondResponse || secondResponse?.length === 0) {
          justDoIt = false
          continue
        }

        results = results.concat(secondResponse)

        if (secondResponse?.length < 500) {
          justDoIt = false
        }
        secondOffset += secondResponse?.length
      }
    }

    results = results.concat(response)
    offset += limit
    currentLastIndex = limit + currentLastIndex < total ? currentLastIndex + limit : total
  } while (results.length < total)

  return results
}

/**
 * Retrieves changes for price list retrieval
 *
 * @returns {Promise<array|null>}
 */
export const getPricesChannels = async () => {
  const defaultListPriceKeys = [
    `"priceList1"`,
    `"priceList2"`,
    `"priceList3"`,
    `"priceList4"`,
    `"priceList5"`,
    `"purchasePrice"`,
    `"minimumSellingPrice"`,
  ].join(",")

  try {
    const listPriceChannels = (
      await commerceApi
        .channels()
        .get({
          queryArgs: {
            where: `key in (${defaultListPriceKeys})`,
          },
        })
        .execute()
    )?.body?.results

    if (listPriceChannels.length === 0) {
      return null
    }

    return listPriceChannels
  } catch (err) {
    console.warn(err)
    return null
  }
}
